import { sum } from 'lodash'
import { getValuesForMonth } from '../../components/common/util/date-util'
import {
  CostFragment,
  CostType,
  PricingModelFragment,
  PurchasedEnergyType,
} from '../../generated/graphql'
import { M } from '../../util/formatters'

const monthIndices = Array.from(Array(12).keys())

export enum EnergyCostType {
  ElectricityCost = 'ElectricityCost',
  ElectricityProfit = 'ElectricityProfit',
  Heating = 'Heating',
  Cooling = 'Cooling',
  Fuel = 'Fuel',
}
type CalculatedCostBase = {
  baseValue: number
  chartValues: number[]
}
export type CalculatedOtherCost = CalculatedCostBase & {
  type: CostType
  providerId?: string
}
export type CalculatedEnergyCost = CalculatedCostBase & {
  energyType: PurchasedEnergyType | EnergyCostType
}
export type CalculatedCost = CalculatedOtherCost | CalculatedEnergyCost

export type EnergyResult = {
  energyType: PurchasedEnergyType
  hourlyValues: number[]
}

/**
 * Create a map for getting the energy cost for given month.
 *
 * @param hourlyEnergy Hourly energy usage in Wh
 * @param monthlyPrices Monthly energy price in €/MWh
 */
function createMapMonthToCost(hourlyEnergy: number[], monthlyPrices: number[]) {
  return (month: number): number => {
    const values = getValuesForMonth(hourlyEnergy, month)
    const totalMonthlyEnergy = sum(values)
    return (monthlyPrices[month] * totalMonthlyEnergy) / M
  }
}

/**
 * Create a map for getting the energy cost for a given year.
 *
 * @param firstYearCost The cost for the first year
 * @param increaseRate The yearly price increase rate, e.g. 0.05.
 * @param discountRate The yearly price discount rate, e.g. 0.05.
 */
function createMapYearToCost(
  firstYearCost: number,
  increaseRate: number,
  discountRate: number,
) {
  return (year: number): number =>
    firstYearCost * ((1 + increaseRate) / (1 + discountRate)) ** year
}

/**
 * Create a map for getting the calculated cost for a given energy result.
 *
 * @param pricingModel The pricing model to be used
 * @param isSoldElectricity Determines whether to use prices or sellingPrices for calculation
 */
export function createMapEnergyResultToCost(
  pricingModel: PricingModelFragment,
  isSoldElectricity: boolean,
): ({ energyType, hourlyValues }: EnergyResult) => CalculatedEnergyCost {
  const chartLenghtInYears = pricingModel.inspectionLengthInYears + 1
  const yearIndices = Array.from(Array(chartLenghtInYears).keys())

  return ({ energyType, hourlyValues }: EnergyResult): CalculatedEnergyCost => {
    const prices = isSoldElectricity
      ? pricingModel.sellingPrices
      : pricingModel.prices

    const monthlyPrices = prices.find(
      (price) => price.energyType === energyType,
    )?.monthlyValues

    if (!monthlyPrices) {
      throw new Error(`Unable to find price for energy type '${energyType}'.`)
    }

    const mapMonthToCost = createMapMonthToCost(hourlyValues, monthlyPrices)
    const monthlyCosts = monthIndices.map(mapMonthToCost)
    const baseValue = sum(monthlyCosts)

    const increaseRate =
      pricingModel.priceIncreaseRates.find(
        (rate) => rate.energyType === energyType,
      )?.yearlyValue ?? 0

    const discountRate = pricingModel.yearlyDiscountRate

    const mapYearToCost = createMapYearToCost(
      baseValue,
      increaseRate,
      discountRate,
    )
    const chartValues = yearIndices.map((yearIndex) =>
      yearIndex === 0 ? 0 : mapYearToCost(yearIndex),
    )

    return {
      energyType,
      baseValue,
      chartValues,
    }
  }
}

function createMapByCostRules(costFragment: CostFragment) {
  return (yearlyValue: number, yearIndex: number) => {
    switch (costFragment.costType) {
      case CostType.Upkeep:
      case CostType.OtherAnnual: {
        if (yearIndex === 0) {
          return 0
        }
        const { endYear } = costFragment
        if (endYear && yearIndex > endYear) {
          return 0
        }
        return yearlyValue
      }
      case CostType.Initial:
      case CostType.OtherOneTime: {
        const occurrenceYear = costFragment.occurrenceYear ?? 0
        if (yearIndex === occurrenceYear) {
          return yearlyValue
        }
        return 0
      }
      case CostType.Renewal:
      case CostType.OtherRecurring: {
        const { recurrencePeriodInYears } = costFragment
        if (!recurrencePeriodInYears) {
          throw new Error(
            'Recurrence period not found for renewal/recurring cost rules mapping, needed for calculation',
          )
        }
        if (yearIndex === 0) {
          return 0
        }
        if (yearIndex % recurrencePeriodInYears === 0 && yearIndex) {
          return yearlyValue
        }
        return 0
      }
      default:
        throw new Error('Cost type not found for rules mapping')
    }
  }
}

/**
 * Create a map for getting the calculated cost for a given energy result.
 *
 * @param pricingModel The pricing model to be used
 */
export function createMapCostToCalculatedCost(
  pricingModel: PricingModelFragment,
): (
  cost: CostFragment,
  multipliers: Record<string, number>,
) => CalculatedOtherCost {
  const chartLenghtInYears = pricingModel.inspectionLengthInYears + 1
  const yearIndices = Array.from(Array(chartLenghtInYears).keys())

  return (
    cost: CostFragment,
    multipliers: Record<string, number>,
  ): CalculatedOtherCost => {
    const buildingMultiplier: number | undefined = cost.providerId
      ? multipliers[cost.providerId]
      : 1
    const baseValue = buildingMultiplier
      ? cost.value * buildingMultiplier
      : cost.value
    const increaseRate =
      cost.costType === CostType.Upkeep
        ? pricingModel.yearlyMaintenanceIncreaseRate
        : 0
    const discountRate = pricingModel.yearlyDiscountRate

    const mapYearToCost = createMapYearToCost(
      baseValue,
      increaseRate,
      discountRate,
    )
    const mapByCostRules = createMapByCostRules(cost)
    const chartValues = yearIndices.map(mapYearToCost).map(mapByCostRules)

    return {
      type: cost.costType,
      baseValue,
      chartValues,
      providerId: cost.providerId ?? undefined,
    }
  }
}
